// Shared download utilities for SERPS reports
// Uses Blob + anchor click — no server required, works in MSW demo mode.

/** Download any string content as a named file. */
function downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: `${mime};charset=utf-8;` });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Download data as CSV. */
export function downloadCsv(
  columns: string[],
  data: Record<string, unknown>[],
  filename: string,
) {
  const escape = (v: unknown) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const header = columns.join(',');
  const rows   = data.map((row) => columns.map((c) => escape(row[c])).join(','));
  downloadBlob([header, ...rows].join('\n'), filename, 'text/csv');
}

/** Generate a styled HTML report and trigger browser download (opens as HTML). */
export function downloadHtmlReport(opts: {
  title:    string;
  subtitle: string;
  school:   string;
  student?: string;
  date:     string;
  sections: Array<{
    heading: string;
    table?: { headers: string[]; rows: string[][] };
    text?:  string;
  }>;
  filename: string;
}) {
  const { title, subtitle, school, student, date, sections, filename } = opts;

  const tableHtml = (headers: string[], rows: string[][]) => `
    <table>
      <thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
      <tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('\n')}</tbody>
    </table>`;

  const body = sections.map((s) => `
    <section>
      <h2>${s.heading}</h2>
      ${s.table ? tableHtml(s.table.headers, s.table.rows) : ''}
      ${s.text  ? `<p class="body-text">${s.text}</p>` : ''}
    </section>`).join('\n');

  const html = `<!DOCTYPE html>
<html lang="ms-BN">
<head>
<meta charset="UTF-8">
<title>${title}</title>
<style>
  body{font-family:'Segoe UI',Arial,sans-serif;margin:0;padding:32px 48px;color:#111827;background:#FAF8F2}
  .header{border-bottom:3px solid #F7D000;padding-bottom:16px;margin-bottom:24px}
  .logo{display:flex;align-items:center;gap:12px;margin-bottom:8px}
  .logo-mark{width:40px;height:40px;background:#F7D000;border-radius:8px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;color:#111}
  h1{margin:0;font-size:22px;font-weight:700;color:#111827}
  .meta{font-size:13px;color:#6B7280;margin-top:4px}
  section{margin-bottom:28px}
  h2{font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#6B7280;margin:0 0 10px;padding-bottom:6px;border-bottom:1px solid #E5E7EB}
  table{width:100%;border-collapse:collapse;font-size:13px;margin-top:8px}
  th{background:#F9FAFB;padding:9px 12px;text-align:left;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#6B7280;border-bottom:2px solid #E5E7EB}
  td{padding:10px 12px;border-bottom:1px solid #F3F4F6;color:#374151}
  tr:nth-child(even) td{background:#FAFAFA}
  .body-text{font-size:13px;color:#374151;line-height:1.7}
  .footer{margin-top:40px;padding-top:16px;border-top:1px solid #E5E7EB;font-size:11px;color:#9CA3AF;display:flex;justify-content:space-between}
  @media print{body{padding:16px 24px}.footer{position:fixed;bottom:16px;left:24px;right:24px}}
</style>
</head>
<body>
  <div class="header">
    <div class="logo">
      <div class="logo-mark">KPM</div>
      <div>
        <div style="font-weight:700;font-size:14px">Kementerian Pendidikan Negara Brunei Darussalam</div>
        <div style="font-size:11px;color:#9CA3AF">Sistem Pengurusan Rekod Sekolah (SERPS)</div>
      </div>
    </div>
    <h1>${title}</h1>
    <div class="meta">
      ${subtitle} &nbsp;·&nbsp; ${school}
      ${student ? ` &nbsp;·&nbsp; ${student}` : ''}
      &nbsp;·&nbsp; ${date}
    </div>
  </div>
  ${body}
  <div class="footer">
    <span>© 2026 Kementerian Pendidikan Negara Brunei Darussalam · SERPS v1.0</span>
    <span>Dijana pada ${new Date().toLocaleString('ms-BN')}</span>
  </div>
</body>
</html>`;

  downloadBlob(html, filename, 'text/html');
}

/** Convenience: download attendance report for a parent's child. */
export function downloadAttendanceReport(opts: {
  studentName: string;
  school:      string;
  month:       string;
  year:        string;
  rows: Array<{ date: string; day: string; status: string; note: string }>;
  summary: { hadir: number; lewat: number; tidakHadir: number; rate: string };
}) {
  const { studentName, school, month, year, rows, summary } = opts;

  downloadHtmlReport({
    title:    `Laporan Kehadiran — ${month} ${year}`,
    subtitle: `Laporan Kehadiran Pelajar`,
    school,
    student: studentName,
    date: `${month} ${year}`,
    filename: `SERPS_LaporanKehadiran_${studentName.replace(/\s+/g,'_')}_${month}${year}.html`,
    sections: [
      {
        heading: 'Ringkasan Bulanan / Monthly Summary',
        table: {
          headers: ['Hari Hadir','Hari Lewat','Hari Tidak Hadir','Kadar Kehadiran'],
          rows: [[String(summary.hadir), String(summary.lewat), String(summary.tidakHadir), summary.rate]],
        },
      },
      {
        heading: 'Rekod Terperinci / Detailed Records',
        table: {
          headers: ['Tarikh','Hari','Status','Catatan'],
          rows: rows.map((r) => [r.date, r.day, r.status, r.note || '—']),
        },
      },
    ],
  });
}

/** Convenience: download academic grades report. */
export function downloadGradesReport(opts: {
  studentName: string;
  school:      string;
  term:        string;
  rows: Array<{ subject: string; teacher: string; grade: string; score: string }>;
}) {
  const { studentName, school, term, rows } = opts;

  downloadHtmlReport({
    title:    `Laporan Akademik — ${term}`,
    subtitle: 'Laporan Prestasi Akademik',
    school,
    student: studentName,
    date: term,
    filename: `SERPS_LaporanAkademik_${studentName.replace(/\s+/g,'_')}_${term.replace(/\s+/g,'_')}.html`,
    sections: [
      {
        heading: 'Gred Mengikut Mata Pelajaran / Grades by Subject',
        table: {
          headers: ['Mata Pelajaran','Guru','Gred','Markah (%)'],
          rows: rows.map((r) => [r.subject, r.teacher, r.grade, r.score]),
        },
      },
    ],
  });
}
